"use client";

import { useEffect, useRef, useState } from "react";
import "./SearchInput.css";
import { csx } from "../../../utils";
import { Input, type BaseInputProps } from "../BaseInput/Input";

export interface SearchInputProps
  extends Omit<
    BaseInputProps,
    | "prefix"
    | "suffix"
    | "value"
    | "onChange"
    | "description"
    | "descriptionPosition"
    | "label"
  > {
  /** The initial value of the input. */
  defaultValue?: string;

  /** Should the clear button be shown when there is text? */
  isClearable?: boolean;

  /** Called when the input value changes. */
  onChange?: (value: string) => void;

  /** Called when the clear button is clicked. */
  onClear?: () => void;

  /** The current value of the input (for controlled usage) */
  value?: string;
}

/**
 * A search input component with optional clear functionality and URL parameter sync.
 *
 * API:
 *  - {@link SearchInputProps}
 *  - extends {@link BaseInputProps}
 */
export const SearchInput = ({
  name = "search",
  placeholder = "Search...",
  autoComplete,
  isClearable = true,
  isDisabled = false,
  error,
  defaultValue,
  value: controlledValue,
  "data-testid": testId = "proton-SearchInput",
  onChange,
  onClear,
}: SearchInputProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState(controlledValue ?? defaultValue ?? "");

  // Sync with controlled value if provided
  useEffect(() => {
    if (controlledValue !== undefined) {
      setValue(controlledValue);
    }
  }, [controlledValue]);

  const handleChange = (newValue: string) => {
    setValue(newValue);
    onChange?.(newValue);
  };

  const handleClear = () => {
    setValue("");
    onChange?.("");
    onClear?.();
    inputRef.current?.focus();
  };

  return (
    <div
      className="proton-SearchInput__wrapper"
      data-testid={testId}
      role="search"
    >
      <Input
        type="search"
        onChange={handleChange}
        value={value}
        name={name}
        placeholder={placeholder}
        autoComplete={autoComplete}
        error={error}
        ref={inputRef}
        isDisabled={isDisabled}
        prefix={
          <svg
            aria-hidden="true"
            width="2.8em"
            height="1.6em"
            viewBox="0 0 24 24"
            onClick={() => inputRef.current?.focus()}
          >
            <title>Magnifying Glass</title>
            <path
              fill="currentColor"
              d="M10.5 18.5a8 8 0 1 1 0-16 8 8 0 0 1 0 16Zm0-1a7 7 0 1 0 0-14 7 7 0 0 0 0 14Zm5.25-.75 4.5 4.5-1.25 1.25-4.5-4.5 1.25-1.25Z"
            />
          </svg>
        }
        suffix={
          isClearable && value.length > 0 ? (
            <button
              type="button"
              className={csx(
                "proton-SearchInput__button",
                "proton-Input__descriptor",
                "proton-Input__suffix"
              )}
              data-testid={`${testId}-clear`}
              onClick={handleClear}
              aria-label="Clear search"
            >
              <svg
                aria-hidden="true"
                viewBox="0 0 50 50"
                width="1.6em"
                height="1.2em"
              >
                <title>Clear</title>
                <path
                  fill="currentColor"
                  d="m37.304 11.282l1.414 1.414l-26.022 26.02l-1.414-1.413z"
                />
                <path
                  fill="currentColor"
                  d="m12.696 11.282l26.022 26.02l-1.414 1.415l-26.022-26.02z"
                />
              </svg>
            </button>
          ) : null
        }
      />
    </div>
  );
};
